<template>
    <form name="form-speaker" id="form-speaker" class="speaker-form" novalidate @submit="onSubmit" @input="markDirty" @change="markDirty">
        <input type="hidden" name="id" v-model="model.id">
        <input type="hidden" name="pic" v-model="model.image">

        <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

        <div v-if="Object.keys(fieldErrors).length" class="form-alert" role="alert" aria-live="assertive">
            <strong>Review the highlighted fields.</strong>
            <span>{{ formErrorMessage }}</span>
        </div>

        <div class="form-row speaker-form-sections">
            <section class="col-lg-6 mb-4 mb-lg-0" aria-labelledby="speaker-details-heading">
                <div class="speaker-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">PROFILE</p>
                            <h2 id="speaker-details-heading">Speaker details</h2>
                        </div>
                        <i class="pi pi-user" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="name">Name <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="name" v-model="model.name" class="form-control" :class="{ 'p-invalid': fieldErrors.name }" aria-describedby="name-help name-error" required />
                        <small id="name-help" class="form-help">Use the speaker's public-facing full name.</small>
                        <small v-if="fieldErrors.name" id="name-error" class="field-error">{{ fieldErrors.name }}</small>
                    </div>

                    <div class="form-group">
                        <label for="companyName">Company name <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="companyName" v-model="model.companyName" class="form-control" :class="{ 'p-invalid': fieldErrors.companyName }" aria-describedby="company-name-error" required />
                        <small v-if="fieldErrors.companyName" id="company-name-error" class="field-error">{{ fieldErrors.companyName }}</small>
                    </div>

                    <div class="form-group">
                        <label for="currentPosition">Current position <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="currentPosition" v-model="model.currentPosition" class="form-control" :class="{ 'p-invalid': fieldErrors.currentPosition }" aria-describedby="current-position-help current-position-error" required />
                        <small id="current-position-help" class="form-help">For example, Chief Executive Officer or Product Director.</small>
                        <small v-if="fieldErrors.currentPosition" id="current-position-error" class="field-error">{{ fieldErrors.currentPosition }}</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="priority">Priority <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="priority" v-model="model.priority" type="number" inputmode="numeric" min="0" class="form-control" :class="{ 'p-invalid': fieldErrors.priority }" aria-describedby="priority-help priority-error" required />
                        <small id="priority-help" class="form-help">Use a whole number. Lower numbers appear first in the speaker list.</small>
                        <small v-if="fieldErrors.priority" id="priority-error" class="field-error">{{ fieldErrors.priority }}</small>
                    </div>
                </div>
            </section>

            <section class="col-lg-6" aria-labelledby="speaker-content-heading">
                <div class="speaker-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">CONTENT</p>
                            <h2 id="speaker-content-heading">Contact and biography</h2>
                        </div>
                        <i class="pi pi-address-book" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="email">Email</label>
                        <InputText id="email" v-model="model.email" type="email" class="form-control" :class="{ 'p-invalid': fieldErrors.email }" aria-describedby="email-help email-error" />
                        <small id="email-help" class="form-help">Optional. Add an address for speaker communications.</small>
                        <small v-if="fieldErrors.email" id="email-error" class="field-error">{{ fieldErrors.email }}</small>
                    </div>

                    <div class="form-group">
                        <label for="phoneNumber">Phone number</label>
                        <InputText id="phoneNumber" v-model="model.phoneNumber" class="form-control" aria-describedby="phone-help" />
                        <small id="phone-help" class="form-help">Optional. Include the country code when available.</small>
                    </div>

                    <div class="form-group">
                        <label for="biography">Biography <span class="required-indicator" aria-hidden="true">*</span></label>
                        <Textarea id="biography" v-model="model.biography" class="form-control" :class="{ 'p-invalid': fieldErrors.biography }" aria-describedby="biography-help biography-error" rows="7" required />
                        <small id="biography-help" class="form-help">Briefly describe the speaker's expertise and relevant experience.</small>
                        <small v-if="fieldErrors.biography" id="biography-error" class="field-error">{{ fieldErrors.biography }}</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="speaker-photo">Profile photo</label>
                        <small class="form-help upload-help">Optional. Upload a JPG, PNG, or WEBP image up to 5 MB.</small>
                        <FileUpload
                            id="speaker-photo"
                            mode="advanced"
                            name="file"
                            :multiple="false"
                            accept="image/jpeg,image/png,image/webp"
                            :maxFileSize="5242880"
                            chooseLabel="Choose photo"
                            uploadLabel="Upload photo"
                            cancelLabel="Remove"
                            v-model="model.uploadFile"
                            :url="uploadUrl"
                            @select="markDirty"
                            @error="onUploadError"
                            @upload="onUploadSuccess"
                        />
                        <div v-if="imagePreviewUrl" class="profile-photo-preview">
                            <img :src="imagePreviewUrl" alt="Current speaker profile photo">
                            <span>Current profile photo</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div class="form-actions">
            <p class="form-actions-note">{{ hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes are saved when you submit.' }}</p>
            <div class="form-actions-buttons">
                <button type="button" class="btn btn-outline-secondary mr-sm-1" @click="onCancel">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ saving ? 'Saving…' : (isNew ? 'Create speaker' : 'Save changes') }}
                </button>
            </div>
        </div>
    </form>

    <ConfirmDialog></ConfirmDialog>
</template>

<script>
import {defineComponent} from 'vue'
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import FileUpload from "primevue/fileupload";
import apiService from "@/services/ApiService";

export default defineComponent({
    name: "SpeakerForm",
    components: {
        InputText, Textarea, FileUpload
    },
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            model: {
                id: '',
                name: '',
                email: '',
                phoneNumber: '',
                companyName: '',
                currentPosition: '',
                biography: '',
                uploadFile: '',
                priority: '',
                image: ''
            },
            saving: false,
            hasUnsavedChanges: false,
            fieldErrors: {},
            uploadUrl: ''
        }
    },
    computed: {
        formErrorMessage() {
            return Object.values(this.fieldErrors)[0];
        },
        imagePreviewUrl() {
            return this.model.image ? `${apiService.defaults.baseURL}/file/${this.model.image}` : '';
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault();
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            let url = '/speaker/edit';
            let successMessage = 'Speaker has been updated';
            let errorMessage = 'Failed to update speaker';
            if(this.isNew){
                url = '/speaker/add';
                successMessage = 'Speaker has been added';
                errorMessage = 'Failed to add speaker';
            }

            const payload = {
                ...this.model,
                priority: Number(this.model.priority)
            };

            apiService.post(url, payload)
                .then(() => {
                    this.hasUnsavedChanges = false;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            if (!this.hasUnsavedChanges) {
                this.$router.push('/speaker');
                return;
            }

            this.$confirm.require({
                message: 'Discard your unsaved speaker changes?',
                header: 'Discard changes',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.$router.push('/speaker')
            });
        },
        markDirty() {
            this.hasUnsavedChanges = true;
            if (Object.keys(this.fieldErrors).length) {
                this.validateForm();
            }
        },
        validateForm() {
            const errors = {};
            const requiredFields = [
                ['name', 'Name is required.'],
                ['companyName', 'Company name is required.'],
                ['currentPosition', 'Current position is required.'],
                ['biography', 'Biography is required.']
            ];

            requiredFields.forEach(([field, message]) => {
                if (!String(this.model[field] || '').trim()) {
                    errors[field] = message;
                }
            });

            const priority = this.model.priority;
            if (priority === '' || priority === null || priority === undefined) {
                errors.priority = 'Priority is required.';
            } else if (!Number.isInteger(Number(priority)) || Number(priority) < 0) {
                errors.priority = 'Priority must be a whole number of 0 or more.';
            }

            if (this.model.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.model.email.trim())) {
                errors.email = 'Enter a valid email address.';
            }

            this.fieldErrors = errors;
            return Object.keys(errors).length === 0;
        },
        onUploadSuccess(event){
            const response = JSON.parse(event.xhr.response);
            this.model.image = response.fileName;
            this.hasUnsavedChanges = true;

            this.$toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Profile photo uploaded.',
                life: 5000
            });
        },
        onUploadError(event){
            let response = {};
            try {
                response = JSON.parse(event.xhr.response);
            } catch (_) {
                response = {};
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Failed to upload photo',
                detail: response.Message || 'Please choose a supported image and try again.',
                life: 5000
            });
        },
        getSpeakerById(id) {
            apiService.get(`/speaker/${id}`)
                .then(response => {
                    this.model = {
                        ...this.model,
                        ...response.data,
                        uploadFile: ''
                    };
                    this.hasUnsavedChanges = false;
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get speaker',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        }
    },
    mounted() {
        this.uploadUrl = `${apiService.defaults.baseURL}/file/upload`;
        if(!this.isNew){
            this.getSpeakerById(this.id);
        }
    }
})
</script>

<style scoped>
.speaker-form {
    max-width: 1280px;
}

.form-required-note {
    color: #5f6570;
    font-size: 0.875rem;
    margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
    color: #c62828;
    font-weight: 700;
}

.form-alert {
    align-items: flex-start;
    background: #fff1f1;
    border: 1px solid #f5c2c7;
    border-radius: 0.5rem;
    color: #9d2525;
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    gap: 0.2rem;
    margin-bottom: 1rem;
    padding: 0.8rem 1rem;
}

.speaker-form-section {
    background: #ffffff;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
    height: 100%;
    padding: 1.5rem;
}

.section-heading {
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.section-heading h2 {
    color: #2b2f36;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
}

.section-eyebrow {
    color: #426cc6;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 0.25rem;
}

.section-heading > .pi {
    color: #426cc6;
    font-size: 1.2rem;
    padding-top: 0.25rem;
}

.form-group label {
    color: #343a40;
    font-size: 0.9rem;
    font-weight: 600;
}

.form-help {
    color: #6c757d;
    display: block;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.35rem;
}

.upload-help {
    margin-bottom: 0.65rem;
}

.field-error {
    color: #c62828;
    display: block;
    font-size: 0.8rem;
    margin-top: 0.35rem;
}

.profile-photo-preview {
    align-items: center;
    color: #5f6570;
    display: flex;
    font-size: 0.8rem;
    gap: 0.65rem;
    margin-top: 0.85rem;
}

.profile-photo-preview img {
    border: 1px solid #e2e6ec;
    border-radius: 50%;
    height: 3rem;
    object-fit: cover;
    width: 3rem;
}

.form-actions {
    align-items: center;
    border-top: 1px solid #e2e6ec;
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
}

.form-actions-note {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0;
}

.form-actions-buttons {
    display: flex;
}

@media (max-width: 767.98px) {
    .speaker-form-section {
        padding: 1.15rem;
    }

    .form-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 1rem;
    }

    .form-actions-buttons {
        justify-content: flex-end;
    }
}
</style>
